import {
    defaultSuggestionCardFilter,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangeStatus } from 'models/changes'
import { useCallback, useEffect } from 'react'
import { useSuggestionDataHandler } from './useSuggestionDataHandler'
import { useTrackChangeCards } from './useTrackChangeCards'

/**
 * Custom hook to handle suggestion card filtering based on the current filter state.
 * @returns {Object} An object containing the processSuggestionData function.
 */
export const useSuggestionCardFilterHandler = () => {
    const { renderSuggestions } = useSuggestionDataHandler()
    const { debouncedUpdateCardPositions } = useTrackChangeCards()
    const {
        isLoadingCards,
        setIsLoadingCards,
        isSuggestionsDataUpdated,
        suggestionCardFilter,
    } = useSuggestionsShallowSelector((s) => ({
        isLoadingCards: s.isLoadingCards,
        setIsLoadingCards: s.setIsLoadingCards,
        isSuggestionsDataUpdated: s.isSuggestionsDataUpdated,
        suggestionCardFilter: s.suggestionCardFilter,
    }))

    const processSuggestionData = useCallback(
        async (resolutionType?: string) => {
            const resolvedSuggestionCardFilter =
                resolutionType || suggestionCardFilter

            switch (resolvedSuggestionCardFilter) {
                case TrackChangeStatus.APPROVED:
                case TrackChangeStatus.REJECTED:
                    renderSuggestions(resolvedSuggestionCardFilter, undefined)
                    break
                case TrackChangeStatus.PENDING:
                    renderSuggestions(
                        resolvedSuggestionCardFilter,
                        TrackChangeStatus.PENDING
                    )
                    break
                case defaultSuggestionCardFilter:
                    await debouncedUpdateCardPositions()
                    break
                default:
                    break
            }
        },
        [suggestionCardFilter, renderSuggestions, debouncedUpdateCardPositions]
    )

    // React to changes in isSuggestionsDataUpdated and isLoadingCards
    useEffect(() => {
        const shouldProcess = !isSuggestionsDataUpdated && isLoadingCards
        if (shouldProcess) {
            processSuggestionData().finally(() => setIsLoadingCards(false))
        }
    }, [
        isSuggestionsDataUpdated,
        processSuggestionData,
        isLoadingCards,
        setIsLoadingCards,
    ])

    return {
        processSuggestionData,
    }
}
